/**
 * Tenant Service
 * 
 * Handles tenant-related database operations.
 * Note: This service does NOT extend BaseService because it needs to access
 * all tenants across the system (super_admin functionality).
 * 
 * RBAC:
 * - super_admin: Can view and manage all tenants
 * - client_admin: Can only view/update their own tenant
 */

import type { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from '../supabase/client';
import { handleSupabaseError } from '../supabase/errorHandler';
import { addBreadcrumb } from '../../lib/sentry';

/**
 * A single onboarding step stored inside tenants.onboarding_progress JSONB
 */
export interface OnboardingStep {
  key: string;
  label: string;
  completed: boolean;
}

/**
 * Shape of the tenants.onboarding_progress JSONB column
 */
export interface OnboardingProgress {
  steps: OnboardingStep[];
}

/**
 * Tenant status — must match the tenants.status CHECK constraint in the database.
 * 'suspended' = deactivated by the super-admin (users of the client cannot sign in).
 */
export const TenantStatus = {
  ACTIVE: 'active',
  SUSPENDED: 'suspended',
  DELETED: 'deleted',
} as const;
export type TenantStatus = (typeof TenantStatus)[keyof typeof TenantStatus];

/** The last change to a tenant's access (who, when, why) — kept in tenants.settings.access. */
export interface TenantAccessChange {
  status: TenantStatus;
  reason: string | null;
  changed_at: string;
  changed_by: string | null;
}

/** tenants.settings — free-form JSON; the engine also reads keys from it (e.g. landing_page). */
export interface TenantSettings {
  access?: TenantAccessChange;
  [key: string]: unknown;
}

/**
 * Tenant database record
 */
export interface Tenant {
  id: string;
  name: string;
  slug: string;
  status: TenantStatus;
  settings?: TenantSettings | null;
  onboarding_progress?: OnboardingProgress | null;
  created_at: string;
  updated_at: string;
}

/**
 * Data for creating a new tenant
 */
export interface CreateTenantData {
  name: string;
  slug: string;
  status?: TenantStatus;
}

/**
 * Data for updating a tenant
 */
export interface UpdateTenantData {
  name?: string;
  slug?: string;
  status?: TenantStatus;
  settings?: TenantSettings;
}

/**
 * Tenant Service Class
 */
class TenantService {
  protected supabase: SupabaseClient;
  protected tableName = 'tenants';

  constructor() {
    this.supabase = supabase;
  }

  /**
   * Get all tenants (super_admin only)
   * @returns Array of all tenants
   */
  async getAll(): Promise<Tenant[]> {
    addBreadcrumb(
      'Fetching all tenants',
      'database',
      'info'
    );

    const { data, error } = await this.supabase
      .from(this.tableName)
      .select('*')
      .order('name');

    if (error) {
      handleSupabaseError(error, {
        operation: 'getAll',
        table: this.tableName,
      });
      throw new Error(`Failed to fetch tenants: ${error.message}`);
    }

    return (data || []) as Tenant[];
  }

  /**
   * Get a single tenant by ID
   * @param id - Tenant ID
   * @returns Tenant or null if not found
   */
  async getById(id: string): Promise<Tenant | null> {
    addBreadcrumb(
      'Fetching tenant by ID',
      'database',
      'info',
      { id }
    );

    const { data, error } = await this.supabase
      .from(this.tableName)
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        // Not found
        return null;
      }
      handleSupabaseError(error, {
        operation: 'getById',
        table: this.tableName,
        extra: { id },
      });
      throw new Error(`Failed to fetch tenant: ${error.message}`);
    }

    return data as Tenant;
  }

  /**
   * Create a new tenant
   * @param tenantData - Tenant data
   * @returns Created tenant
   */
  async create(tenantData: CreateTenantData): Promise<Tenant> {
    addBreadcrumb(
      'Creating tenant',
      'database',
      'info',
      { name: tenantData.name }
    );

    const { data, error } = await this.supabase
      .from(this.tableName)
      .insert({
        ...tenantData,
        status: tenantData.status || 'active',
      })
      .select()
      .single();

    if (error) {
      handleSupabaseError(error, {
        operation: 'create',
        table: this.tableName,
        extra: tenantData,
      });
      throw new Error(`Failed to create tenant: ${error.message}`);
    }

    return data as Tenant;
  }

  /**
   * Update a tenant
   * @param id - Tenant ID
   * @param tenantData - Updated tenant data
   * @returns Updated tenant
   */
  async update(id: string, tenantData: UpdateTenantData): Promise<Tenant> {
    addBreadcrumb(
      'Updating tenant',
      'database',
      'info',
      { id, ...tenantData }
    );

    const { data, error } = await this.supabase
      .from(this.tableName)
      .update(tenantData)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      handleSupabaseError(error, {
        operation: 'update',
        table: this.tableName,
        extra: { id, ...tenantData },
      });
      throw new Error(`Failed to update tenant: ${error.message}`);
    }

    return data as Tenant;
  }

  /**
   * Deactivate a tenant: its users can no longer sign in (enforced at login and by the engine).
   * The reason, time and actor are kept in settings.access.
   */
  async deactivate(id: string, reason: string, actorId: string | null): Promise<Tenant> {
    return this.setAccess(id, 'suspended', reason, actorId);
  }

  /** Reactivate a tenant; its users can sign in again. */
  async activate(id: string, actorId: string | null): Promise<Tenant> {
    return this.setAccess(id, 'active', null, actorId);
  }

  /** Status change + access record, merged into the existing settings (other keys are kept). */
  private async setAccess(id: string, status: TenantStatus, reason: string | null, actorId: string | null): Promise<Tenant> {
    const current = await this.getById(id);
    if (!current) throw new Error('Tenant not found');
    const access: TenantAccessChange = { status, reason, changed_at: new Date().toISOString(), changed_by: actorId };
    return this.update(id, { status, settings: { ...(current.settings ?? {}), access } });
  }

  /** Number of user profiles per tenant id. Paged: one PostgREST response is capped at 1000 rows. */
  async getUserCounts(): Promise<Record<string, number>> {
    const PAGE = 1000;
    const counts: Record<string, number> = {};
    for (let from = 0; ; from += PAGE) {
      const { data, error } = await this.supabase.from('profiles').select('tenant_id').order('id').range(from, from + PAGE - 1);
      if (error) {
        handleSupabaseError(error, { operation: 'getUserCounts', table: 'profiles' });
        throw new Error(`Failed to count users: ${error.message}`);
      }
      const rows = (data ?? []) as { tenant_id: string | null }[];
      for (const row of rows) {
        if (row.tenant_id) counts[row.tenant_id] = (counts[row.tenant_id] ?? 0) + 1;
      }
      if (rows.length < PAGE) return counts;
    }
  }

  /**
   * Upload tenant logo to Supabase Storage
   * @param tenantId - Tenant ID
   * @param file - Logo file
   * @returns Public URL of the uploaded logo
   */
  async uploadLogo(tenantId: string, file: File): Promise<string> {
    addBreadcrumb(
      'Uploading tenant logo',
      'storage',
      'info',
      { tenantId, fileName: file.name }
    );

    // Generate storage path: branding/{tenant_id}/logo_{timestamp}_{filename}
    const timestamp = Date.now();
    const storagePath = `branding/${tenantId}/logo_${timestamp}_${file.name}`;

    // Upload to Supabase Storage
    const { data, error } = await this.supabase.storage
      .from('branding')
      .upload(storagePath, file, {
        cacheControl: '3600',
        upsert: false,
      });

    if (error) {
      handleSupabaseError(error, {
        operation: 'uploadLogo',
        extra: { tenantId, fileName: file.name },
      });
      throw new Error(`Failed to upload logo: ${error.message}`);
    }

    // Get public URL
    const { data: urlData } = this.supabase.storage
      .from('branding')
      .getPublicUrl(data.path);

    return urlData.publicUrl;
  }

  /**
   * Get the onboarding progress for a specific tenant
   * @param tenantId - Tenant ID
   * @returns OnboardingProgress or null if not set
   */
  async getOnboardingProgress(tenantId: string): Promise<OnboardingProgress | null> {
    const { data, error } = await this.supabase
      .from(this.tableName)
      .select('onboarding_progress')
      .eq('id', tenantId)
      .single();

    if (error) {
      console.error('Failed to fetch onboarding progress:', error);
      return null;
    }

    return (data?.onboarding_progress as OnboardingProgress) ?? null;
  }

  /**
   * Mark a specific onboarding step as completed
   * @param tenantId - Tenant ID
   * @param stepKey - The key of the step to mark complete (e.g. 'source')
   */
  async completeOnboardingStep(tenantId: string, stepKey: string): Promise<void> {
    // Fetch current progress first
    const current = await this.getOnboardingProgress(tenantId);
    if (!current) return;

    const updatedSteps = current.steps.map((s) =>
      s.key === stepKey ? { ...s, completed: true } : s
    );

    const { error } = await this.supabase
      .from(this.tableName)
      .update({ onboarding_progress: { ...current, steps: updatedSteps } })
      .eq('id', tenantId);

    if (error) {
      throw new Error(`Failed to update onboarding step: ${error.message}`);
    }
  }

  /**
   * Delete tenant logo from Supabase Storage
   * @param logoUrl - Logo URL to delete
   */
  async deleteLogo(logoUrl: string): Promise<void> {
    if (!logoUrl) return;

    try {
      // Extract path from URL
      const url = new URL(logoUrl);
      const pathParts = url.pathname.split('/');
      const bucketIndex = pathParts.indexOf('branding');
      if (bucketIndex === -1) return;

      const storagePath = pathParts.slice(bucketIndex + 1).join('/');

      addBreadcrumb(
        'Deleting tenant logo',
        'storage',
        'info',
        { storagePath }
      );

      const { error } = await this.supabase.storage
        .from('branding')
        .remove([storagePath]);

      if (error) {
        console.error('Failed to delete logo:', error);
        // Don't throw error for logo deletion failures
      }
    } catch (err) {
      console.error('Error parsing logo URL:', err);
      // Don't throw error for URL parsing failures
    }
  }
}

// Export singleton instance
export const tenantService = new TenantService();
export default tenantService;
